import React, { useEffect, useRef, useCallback, useState } from 'react';
import * as d3 from 'd3';
import { Prediction } from '../../types/predictions';
import { Theme } from '../../theme';

// Import the prediction styles for consistent coloring
const predictionStyles = {
  colors: {
    success: 'rgba(76, 175, 80, 0.9)',  // Green
    successSolid: 'rgba(76, 175, 80, 1)',
    miss: 'rgba(244, 67, 54, 0.9)',     // Red  
    missSolid: 'rgba(244, 67, 54, 1)',
    pending: 'rgba(255, 152, 0, 0.9)',  // Orange
    pendingSolid: 'rgba(255, 152, 0, 1)',
    updated: 'rgba(139, 92, 246, 0.9)',  // Purple
    updatedSolid: 'rgba(139, 92, 246, 1)',
    // Bet prediction colors (matching desktop)
    betWinnerSolid: 'rgba(34, 197, 94, 1)', // Green for bet winners
    betLoserSolid: 'rgba(239, 68, 68, 1)',  // Red for bet losers
  }
};

// Helper function to format prices for chart labels (compact but readable)
const formatChartPrice = (price: number): string => {
  // Handle negative values
  if (price < 0) {
    return `-${formatChartPrice(Math.abs(price))}`;
  }
  
  if (price >= 1000000) {
    // For millions: $1M, $1.1M, $13M
    const millions = price / 1000000;
    return millions >= 10 ? `$${millions.toFixed(0)}M` : `$${millions.toFixed(1)}M`;
  } else if (price >= 1000) {
    // For thousands: $100k, $112k
    return `$${(price / 1000).toFixed(0)}k`;
  } else {
    // For smaller numbers: $500, $0, etc.
    return `$${price.toFixed(0)}`;
  }
};

// Helper function to get status-based color
const getStatusColor = (prediction: Prediction, solid = false) => {
  if (prediction.success_status === 'success') {
    return solid ? predictionStyles.colors.successSolid : predictionStyles.colors.success;
  } else if (prediction.success_status === 'miss') {
    return solid ? predictionStyles.colors.missSolid : predictionStyles.colors.miss;
  } else if (prediction.success_status === 'updated') {
    return solid ? predictionStyles.colors.updatedSolid : predictionStyles.colors.updated;
  } else {
    return solid ? predictionStyles.colors.pendingSolid : predictionStyles.colors.pending;
  }
};

// Helper function to get status text
const getStatusText = (prediction: Prediction): string => {
  switch (prediction.success_status) {
    case 'success': return '✓ HIT';
    case 'miss': return '✗ MISS';
    case 'updated': return '🔄 UPDATED';
    default: return '⏳ PENDING';
  }
};

// Helper function for collision detection - prevents overlapping labels
const resolveCollisions = (labels: Array<{x: number, y: number, height: number, text: string, id: string, color: string, type?: string, participant?: any}>) => {
  const resolvedLabels = [...labels];
  const minSpacing = 3; // Minimum spacing between labels
  
  // Sort by Y position
  resolvedLabels.sort((a, b) => a.y - b.y);
  
  // Check for overlaps and adjust positions
  for (let i = 1; i < resolvedLabels.length; i++) {
    const current = resolvedLabels[i];
    const previous = resolvedLabels[i - 1];
    
    const previousBottom = previous.y + (previous.height / 2);
    const currentTop = current.y - (current.height / 2);
    
    if (currentTop < previousBottom + minSpacing) {
      // Collision detected - move current label down
      current.y = previousBottom + minSpacing + (current.height / 2);
    }
  }
  
  return resolvedLabels;
};

// Helper function to add text labels with black background (like main chart)
const addLabelWithBackground = (
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>,
  x: number,
  y: number,
  text: string,
  color: string,
  textAnchor: 'start' | 'middle' | 'end' = 'end',
  onClick?: () => void,
  fontSize: string = '13px',
  fontWeight: string = '600'
) => {
  // Create group for text and background
  const labelGroup = chartArea.append("g");
  
  // Add the text element (temporarily to measure)
  const textElement = labelGroup.append("text")
    .attr("x", x)
    .attr("y", y)
    .style("fill", color)
    .style("font-size", fontSize)
    .style("font-family", "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif")
    .style("font-weight", fontWeight)
    .style("opacity", 0.9)
    .style("text-anchor", textAnchor)
    .style("cursor", "pointer")
    .text(text);
  
  // Get text dimensions for background
  const bbox = (textElement.node() as SVGTextElement).getBBox();
  const padding = 4;
  
  // Insert background rectangle before text
  labelGroup.insert("rect", ":first-child")
    .attr("x", bbox.x - padding)
    .attr("y", bbox.y - padding)
    .attr("width", bbox.width + 2 * padding)
    .attr("height", bbox.height + 2 * padding)
    .attr("rx", 3)
    .attr("ry", 3)
    .style("fill", "rgba(0, 0, 0, 0.8)")
    .style("stroke", "rgba(255, 255, 255, 0.2)")
    .style("stroke-width", 1);
  
  // Add click handler if provided
  if (onClick) {
    labelGroup
      .style("cursor", "pointer")
      .on("click", function(event) {
        event.stopPropagation();
        onClick();
      });
  }
  
  return labelGroup;
};

// Helper function to find crossing point (borrowed from desktop version)
const findCrossingPoint = (
  chartData: Array<{ time: Date; value: number }>,
  prediction: Prediction
): { time: Date; value: number } | null => {
  if (!chartData.length) return null;

  let targetPrice: number | null = null;
  
  if (prediction.prediction_type === 'target') {
    const priceValue = prediction.price_target ?? prediction.price_min ?? prediction.price_prediction;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'top') {
    // TOP predictions use price_max or fallback to price_target
    const priceValue = prediction.price_max ?? prediction.price_target;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'bottom') {
    // BOTTOM predictions use price_min or fallback to price_target  
    const priceValue = prediction.price_min ?? prediction.price_target;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  } else if (prediction.prediction_type === 'pricetimerange') {
    targetPrice = prediction.price_min !== undefined ? prediction.price_min : null;
  } else if (prediction.prediction_type === 'range') {
    targetPrice = prediction.price_min !== undefined ? prediction.price_min : null; // Use min as the threshold that was reached
  } else if (prediction.prediction_type === 'bet') {
    // BET predictions use price_prediction, price_target, or price_min for the bet amount
    const priceValue = prediction.price_prediction ?? prediction.price_target ?? prediction.price_min;
    targetPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
  }

  if (targetPrice === null || targetPrice === undefined) return null;

  // Sort data chronologically
  const sortedData = [...chartData].sort((a, b) => a.time.getTime() - b.time.getTime());
  
  // Get the valid time range for this prediction
  const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
  
  // Find where price crosses the target WITHIN the valid time range
  for (let i = 1; i < sortedData.length; i++) {
    const prev = sortedData[i-1];
    const curr = sortedData[i];
    
    // Skip if current point is outside the prediction's time range
    if (startDate && curr.time < startDate) continue;
    
    // Check if price crosses target between these points
    if (prev.value < targetPrice && curr.value >= targetPrice) {
      return curr;
    }
  }
  
  return null;
};

interface MobilePriceChartProps {
  predictions: Prediction[];
  activePredictions: number[];
  chartData: Array<{ time: string; value: number }>;
  isDarkMode: boolean;
  theme: Theme;
  // ARCHIVED: Contest prop removed (April 2026 contest ended)
  // userContestPrediction?: number | null;
  userContestPrediction?: number | null;
  userHandle?: string;
}

export const MobilePriceChart: React.FC<MobilePriceChartProps> = ({
  predictions,
  activePredictions,
  chartData,
  isDarkMode,
  theme,
  // ARCHIVED: Contest param removed (April 2026 contest ended)
  // userContestPrediction,
  userContestPrediction,
  userHandle
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const zoomRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown>>();
  const transformRef = useRef<d3.ZoomTransform>(d3.zoomIdentity);

  // 🐰 EASTER EGG STATE (Mobile)
  const [easterEggs, setEasterEggs] = useState<Array<{
    id: string;
    x: number;
    y: number;
    vx: number;
    vy: number;
    collected: boolean;
    color: string;
  }>>([]);
  const [easterEggCollected, setEasterEggCollected] = useState(0);
  const [showEasterEggPopup, setShowEasterEggPopup] = useState(false);
  const [showEasterEggInstructions, setShowEasterEggInstructions] = useState(false);
  const [isEasterEggHuntActive, setIsEasterEggHuntActive] = useState(false);
  const [bitcoinExplosions, setBitcoinExplosions] = useState<Array<{
    id: string;
    x: number;
    y: number;
    timestamp: number;
  }>>([]);
  const easterEggAnimationRef = useRef<number>();
  const easterEggSpawnedRef = useRef<boolean>(false);

  // Reset zoom to default view
  const resetZoom = useCallback(() => {
    if (!svgRef.current || !zoomRef.current) return;
    
    const svg = d3.select(svgRef.current);
    svg.transition()
      .duration(750)
      .call(zoomRef.current.transform, d3.zoomIdentity);
    
    transformRef.current = d3.zoomIdentity;
  }, []);

  // 🐰 EASTER EGG LOGIC: Detect when Easter Bunny prediction (ID 331) is active (Mobile)
  useEffect(() => {
    const isEasterBunnyActive = activePredictions?.includes(331);
      
      if (isEasterBunnyActive && !easterEggSpawnedRef.current) {
      // Check if user already found ALL eggs in this session (more lenient)
      const foundData = localStorage.getItem('proofofprice-easter-egg-2026');
      const alreadyFoundAll = foundData && JSON.parse(foundData)?.allFound;
      if (alreadyFoundAll) return;
      
      // Mark as spawned to prevent respawning
      easterEggSpawnedRef.current = true;
      
      // Get proper mobile viewport dimensions
      const screenWidth = Math.min(window.innerWidth || 400, document.documentElement.clientWidth || 400);
      const screenHeight = Math.min(window.innerHeight || 600, document.documentElement.clientHeight || 600);
      
      // Spawn Easter eggs! (21 like Bitcoin's supply)
      const newEggs = Array.from({ length: 21 }, (_, i) => ({
        id: `mobile-egg-${Date.now()}-${i}`, // Unique IDs with timestamp
        x: Math.random() * (screenWidth - 60) + 30, // Keep away from edges
        y: -80 - Math.random() * 200, // Start well above screen
        vx: (Math.random() - 0.5) * 2, // Slightly more horizontal movement
        vy: Math.random() * 2 + 1, // Good downward velocity
        collected: false,
        color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'][i % 6]
      }));
      
      setEasterEggs(newEggs);
      
      // Show instructions popup
      setShowEasterEggInstructions(true);
      setTimeout(() => setShowEasterEggInstructions(false), 4000);
      
      // Start animation with better mobile optimization
      let animationId: number;
      const animate = () => {
        setEasterEggs(prevEggs => {
          const updatedEggs = prevEggs.map(egg => {
            if (egg.collected) return egg;
            
            const newY = egg.y + egg.vy;
            const newX = egg.x + egg.vx;
            
            // Remove eggs that fall below screen (with buffer)
            if (newY > screenHeight + 150) {
              return { ...egg, collected: true };
            }
            
            // Bounce off sides with proper bounds
            let newVx = egg.vx;
            if (newX <= 30 || newX >= screenWidth - 30) {
              newVx = -egg.vx * 0.9; // Slight damping on bounce
            }
            
            return {
              ...egg,
              x: Math.max(30, Math.min(newX, screenWidth - 30)),
              y: newY,
              vx: newVx,
              vy: Math.min(egg.vy + 0.1, 8) // Gravity with max fall speed
            };
          });
          
          // Keep spawning new eggs until we have enough active ones
          const activeEggs = updatedEggs.filter(egg => !egg.collected);
          if (activeEggs.length < 6 && Math.random() < 0.12 && easterEggSpawnedRef.current) { // 12% chance each frame, keep 6 active
            const newEgg = {
              id: `mobile-respawn-egg-${Date.now()}-${Math.random()}`,
              x: Math.random() * (screenWidth - 60) + 30,
              y: -50 - Math.random() * 100,
              vx: (Math.random() - 0.5) * 2,
              vy: Math.random() * 1.5 + 0.8,
              collected: false,
              color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD'][Math.floor(Math.random() * 6)]
            };
            updatedEggs.push(newEgg);
          }
          
          // Continue animation
          if (easterEggSpawnedRef.current) {
            animationId = requestAnimationFrame(animate);
            easterEggAnimationRef.current = animationId;
          }
          
          return updatedEggs;
        });
      };
      
        animate();
      } else if (!isEasterBunnyActive && easterEggSpawnedRef.current) {
        // Clean up when prediction becomes inactive
        easterEggSpawnedRef.current = false;
        setEasterEggs([]);
        if (easterEggAnimationRef.current) {
          cancelAnimationFrame(easterEggAnimationRef.current);
          easterEggAnimationRef.current = undefined;
        }
      }
  }, [activePredictions]);

  // 🐰 EASTER EGG: Handle egg collection with Bitcoin explosion (Mobile)
  const handleEggClick = useCallback((eggId: string) => {
    const clickedEgg = easterEggs.find(egg => egg.id === eggId);
    if (!clickedEgg || clickedEgg.collected) return;
    
    // Create Bitcoin explosion at egg position
    const explosion = {
      id: `mobile-explosion-${Date.now()}-${Math.random()}`,
      x: clickedEgg.x,
      y: clickedEgg.y,
      timestamp: Date.now()
    };
    
    setBitcoinExplosions(prev => [...prev, explosion]);
    
    // Remove explosion after 2 seconds
    setTimeout(() => {
      setBitcoinExplosions(prev => prev.filter(e => e.id !== explosion.id));
    }, 2000);
    
    setEasterEggs(prevEggs => 
      prevEggs.map(egg => 
        egg.id === eggId ? { ...egg, collected: true } : egg
      )
    );
    
    const newCollected = easterEggCollected + 1;
    setEasterEggCollected(newCollected);
    
    // Add haptic feedback for mobile devices
    if ('vibrate' in navigator) {
      navigator.vibrate(100); // Short vibration
    }
    
    // If they collected 21 eggs, show success popup!
    if (newCollected >= 21) {
      setShowEasterEggPopup(true);
      setIsEasterEggHuntActive(false); // Reset hunt state so it can be played again
      
      // Mark as found for this session but allow replay later
      localStorage.setItem('proofofprice-easter-egg-2026', JSON.stringify({ 
        allFound: true, 
        timestamp: Date.now(),
        collectedCount: newCollected 
      }));
      
      // Clean up animation
      setEasterEggs([]);
      easterEggSpawnedRef.current = false;
      if (easterEggAnimationRef.current) {
        cancelAnimationFrame(easterEggAnimationRef.current);
        easterEggAnimationRef.current = undefined;
      }
    }
  }, [easterEggs, easterEggCollected]);

  // 🐰 EASTER EGG CLEANUP: Clean up animation on unmount (Mobile)
  useEffect(() => {
    return () => {
      if (easterEggAnimationRef.current) {
        cancelAnimationFrame(easterEggAnimationRef.current);
      }
    };
  }, []);

  // 🐰 EASTER EGG: Listen for activation event from prediction click (Mobile)
  useEffect(() => {
    const handleEasterEggActivation = (event: CustomEvent) => {
      console.log('🐰 Mobile: Easter egg activation received:', event.detail);
      
      // Don't restart if hunt is already active
      if (isEasterEggHuntActive) {
        console.log('🐰 Hunt already active, ignoring click');
        return;
      }
      
      // Mark hunt as active
      setIsEasterEggHuntActive(true);
      
      // Show instructions first
      setShowEasterEggInstructions(true);
      
      // Hide instructions after 3 seconds and start the hunt
      setTimeout(() => {
        setShowEasterEggInstructions(false);
        startEasterEggHunt();
      }, 3000);
    };

    const startEasterEggHunt = () => {
      console.log('🐰 Mobile: Starting Easter egg hunt!');
      easterEggSpawnedRef.current = true;
      setEasterEggCollected(0);
      
      // Create 21 eggs falling from different positions (slower physics)
      const eggs = Array.from({ length: 21 }, (_, i) => ({
        id: `mobile-egg-${i}`,
        x: Math.random() * (window.innerWidth - 60) + 30,
        y: -50 - (i * 150), // More spacing between eggs
        vx: (Math.random() - 0.5) * 0.8, // Slower horizontal movement
        vy: Math.random() * 0.5 + 0.3, // Much slower initial fall speed
        collected: false,
        color: `hsl(${Math.random() * 360}, 80%, 60%)` // Random egg colors
      }));
      
      setEasterEggs(eggs);
      
      // Start animation
      const animate = () => {
        if (!easterEggSpawnedRef.current) return;
        
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        setEasterEggs(prevEggs => {
          const updatedEggs = prevEggs.map(egg => {
            if (egg.collected) return egg;
            
            const newY = egg.y + egg.vy;
            const newX = egg.x + egg.vx;
            
            // Remove eggs that fall below screen
            if (newY > screenHeight + 150) {
              return { ...egg, collected: true };
            }
            
            // Bounce off sides
            let newVx = egg.vx;
            if (newX <= 30 || newX >= screenWidth - 30) {
              newVx = -egg.vx * 0.9;
            }
            
            return {
              ...egg,
              x: Math.max(30, Math.min(newX, screenWidth - 30)),
              y: newY,
              vx: newVx,
              vy: Math.min(egg.vy + 0.04, 3) // Much slower gravity and max speed
            };
          });
          
          // Continue animation if there are active eggs
          const hasActiveEggs = updatedEggs.some(egg => !egg.collected);
          if (hasActiveEggs) {
            requestAnimationFrame(animate);
          } else {
            // All eggs are gone (collected or fallen), but hunt not completed
            console.log('🐰 All eggs gone, resetting hunt state');
            setIsEasterEggHuntActive(false);
            easterEggSpawnedRef.current = false;
          }
          
          return updatedEggs;
        });
      };
      
      animate();
    };

    // Add event listener
    window.addEventListener('activateEasterEgg', handleEasterEggActivation as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('activateEasterEgg', handleEasterEggActivation as EventListener);
    };
  }, [isEasterEggHuntActive]);

  // Redraw chart function that can be called by zoom
  const redrawChart = useCallback((transform: d3.ZoomTransform = d3.zoomIdentity) => {
    if (!svgRef.current || chartData.length === 0) return;


    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Mobile-optimized dimensions with moderate space for prediction labels
    const margin = { top: 15, right: 40, bottom: 25, left: 45 }; // Reduced right margin from 80px to 40px
    const width = svgRef.current.clientWidth - margin.left - margin.right;
    const height = svgRef.current.clientHeight - margin.top - margin.bottom;

    const g = svg.append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    // Add clipping path to hide elements outside chart area
    const clipId = `chart-clip-${Math.random().toString(36).substr(2, 9)}`;
    const defs = svg.append("defs");
    
    defs.append("clipPath")
      .attr("id", clipId)
      .append("rect")
      .attr("x", 0)
      .attr("y", 0)
      .attr("width", width)
      .attr("height", height);

    // Add green glow filter for successful predictions
    const successFilter = defs.append("filter")
      .attr("id", "success-glow-mobile")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");
    
    successFilter.append("feGaussianBlur")
      .attr("in", "SourceGraphic")
      .attr("stdDeviation", "3")
      .attr("result", "blur");
    
    successFilter.append("feFlood")
      .attr("flood-color", "#4CAF50")
      .attr("flood-opacity", "0.5")
      .attr("result", "color");
    
    successFilter.append("feComposite")
      .attr("in", "color")
      .attr("in2", "blur")
      .attr("operator", "in")
      .attr("result", "glow");
    
    const successMerge = successFilter.append("feMerge");
    successMerge.append("feMergeNode").attr("in", "glow");
    successMerge.append("feMergeNode").attr("in", "SourceGraphic");

    const chartArea = g.append("g")
      .attr("clip-path", `url(#${clipId})`);

    // Parse and sort data
    const parseTime = d3.timeParse("%Y-%m-%d");
    const processedData = chartData
      .map(d => ({
        ...d,
        time: parseTime(d.time) || new Date(d.time),
        value: +d.value
      }))
      .filter(d => d.value > 0)
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    // Debug: Log invalid dates
    processedData.forEach(d => {
      if (isNaN(d.time.getTime())) {
        console.warn('Invalid date:', d.time, d);
      }
    });

    if (processedData.length === 0) return;

    // ULTRA-COMPACT VIEWPORT: Get 0 level super close to X-axis
    const prices = processedData.map(d => d.value);
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const priceRange = maxPrice - minPrice;
    
    // Get active prediction prices to adjust Y-axis if needed
    const activePredictionPrices = activePredictions
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p)
      .map(p => {
        if (p!.prediction_type === 'target') {
          // Target predictions can have price in price_target, price_min, or price_prediction
          const priceValue = p!.price_target ?? p!.price_min ?? p!.price_prediction;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'range') {
          // For range predictions, include both min and max prices
          const minPrice = typeof p!.price_min === 'string' ? parseFloat(p!.price_min) : (p!.price_min ?? null);
          const maxPrice = typeof p!.price_max === 'string' ? parseFloat(p!.price_max) : (p!.price_max ?? null);
          return [minPrice, maxPrice].filter(price => price !== null && isFinite(price));
        } else if (p!.prediction_type === 'pricetimerange') {
          // ✅ FIX: Handle pricetimerange predictions correctly - they use price_min
          const priceValue = p!.price_min ?? p!.price_target;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'top') {
          // TOP predictions use price_max or fallback to price_target/price_min
          const priceValue = p!.price_max ?? p!.price_target ?? p!.price_min;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'bottom') {
          // BOTTOM predictions use price_min or fallback to price_target
          const priceValue = p!.price_min ?? p!.price_target ?? p!.price_prediction;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        } else if (p!.prediction_type === 'bet') {
          // BET predictions use price_prediction, price_target, or price_min for the bet amount
          const priceValue = p!.price_prediction ?? p!.price_target ?? p!.price_min;
          return typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        }
        return null;
      })
      .flat() // Flatten array to handle range predictions that return arrays
      .filter(price => price !== null && price !== undefined && isFinite(price)) as number[];
    
    
    // Include prediction prices in the range calculation
    const allPrices = activePredictionPrices.length > 0 ? [...prices, ...activePredictionPrices] : prices;
    const finalMinPrice = Math.min(...allPrices);
    const finalMaxPrice = Math.max(...allPrices);
    const finalPriceRange = finalMaxPrice - finalMinPrice;
    
    // Ultra-minimal Y range - get 0 super close to X-axis, but expand for predictions
    const padding = Math.max(finalPriceRange * 0.15, 5000); // Increased padding from 0.1 to 0.15 and min from 1000 to 5000
    let yMin = finalMinPrice - padding; // Allow negative values for predictions like Peter Zeihan's -$1000
    let yMax = Math.max(finalMaxPrice + padding, 200000); // Expand ceiling if predictions require it
    
    // ✅ FIX: Prevent unrealistic negative values in Y-axis (limit to -$1000)
    if (yMin < -1000) {
      yMin = -1000; // Hard limit at -$1000 (don't go lower than this)
    }
    
    // ✅ FIX: Dynamic Y-axis expansion for high-value predictions (like Samson Mow's $1M, Chamath's $1.14M)
    if (activePredictionPrices.some(price => price >= 1000000)) {
      // For 1M+ predictions, expand to 1.5M to give more space for very high predictions
      yMax = Math.max(yMax, 1500000);
    } else if (activePredictionPrices.some(price => price > 300000)) {
      yMax = Math.max(yMax, 400000);
    }
    

    // Debug: Log Peter Zeihan's prediction specifically if it's active
    const peterZeihanPrediction = predictions.find(p => p.influencer?.name?.toLowerCase().includes('zeihan'));
    if (peterZeihanPrediction && activePredictions.includes(peterZeihanPrediction.id)) {
      console.log('Peter Zeihan prediction debug:', {
        id: peterZeihanPrediction.id,
        price_prediction: peterZeihanPrediction.price_prediction,
        price_min: peterZeihanPrediction.price_min,
        includedInActivePrices: activePredictionPrices.includes(-1000),
        yMin: yMin,
        yMax: yMax
      });
    }

    // Create smart time window: Always start from 2019
    let timeExtent = d3.extent(processedData, d => d.time) as [Date, Date];
    
    // Get active prediction dates
    const allActiveIds = [...activePredictions];
    
    const activePredictionDates: Date[] = allActiveIds
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p)
      .flatMap(p => {
        const dates: Date[] = [];
        
        // For sentiment predictions, use target_time or prediction_made
        if (p!.prediction_type === 'sentiment') {
          const sentimentDate = p!.target_time
            ? new Date(p!.target_time)
            : (p!.prediction_made
                ? new Date(p!.prediction_made)
                : (p!.created_at ? new Date(p!.created_at) : null));
          if (sentimentDate && !isNaN(sentimentDate.getTime())) {
            dates.push(sentimentDate);
            console.log('Adding sentiment date to viewport:', {
              id: p!.id,
              expert: p!.influencer?.name,
              sentimentDate: sentimentDate
            });
          }
        }
        // For pricetimerange predictions, use start_date and end_date
        else if (p!.prediction_type === 'pricetimerange' && p!.start_date && p!.end_date) {
          dates.push(new Date(p!.start_date));
          dates.push(new Date(p!.end_date));
        }
        // For TOP and BOTTOM predictions, use start_date and extend beyond current chart
        else if ((p!.prediction_type === 'top' || p!.prediction_type === 'bottom') && p!.start_date) {
          dates.push(new Date(p!.start_date));
          // Extend the time window beyond current date for TOP/BOTTOM predictions
          const futureDate = new Date();
          futureDate.setFullYear(futureDate.getFullYear() + 2); // Extend 2 years into future
          dates.push(futureDate);
        }
        // For bet predictions, use start_date and end_date
        else if (p!.prediction_type === 'bet') {
          if (p!.start_date) dates.push(new Date(p!.start_date));
          if (p!.end_date) dates.push(new Date(p!.end_date));
        }
        // For other predictions, use target_time or end_date
        else if (p!.target_time) {
          dates.push(new Date(p!.target_time));
        }
        else if (p!.end_date) {
          dates.push(new Date(p!.end_date));
        }
        
        return dates.filter(date => !isNaN(date.getTime()));
      });
    
    if (activePredictionDates.length > 0) {
      // Smart time window: Show from 2015 + prediction period with extra padding for labels
      const latestDataDate = timeExtent[1]; // Most recent price data
      const latestPrediction = new Date(Math.max(...activePredictionDates.map(d => d.getTime())));
      const earliestPrediction = new Date(Math.min(...activePredictionDates.map(d => d.getTime())));
      
      // Start earlier for better visibility of left-side labels - go back to earliest prediction or 2013 for historical sentiments
      const startOf2015 = new Date('2015-01-01');
      const startOf2013 = new Date('2013-01-01'); // For very historical sentiment tweets like Saylor
      
      // If we have very old predictions (like 2013 sentiment), start from 2013, otherwise 2015
      const hasHistoricalPrediction = earliestPrediction.getFullYear() <= 2014;
      const smartStartDate = hasHistoricalPrediction 
        ? new Date(Math.max(startOf2013.getTime(), timeExtent[0].getTime()))
        : new Date(Math.max(startOf2015.getTime(), timeExtent[0].getTime()));
      
      console.log('Smart time window calculation:', {
        earliestPrediction: earliestPrediction,
        latestPrediction: latestPrediction,
        hasHistoricalPrediction: hasHistoricalPrediction,
        smartStartDate: smartStartDate,
        chartDataStart: timeExtent[0],
        chartDataEnd: timeExtent[1]
      });
      
      // End at prediction date with more padding for labels
      const paddingMonths = 12; // Increased from 6 to 12 months
      const smartEndDate = new Date(latestPrediction.getTime() + (paddingMonths * 30 * 24 * 60 * 60 * 1000));
      
      timeExtent = [smartStartDate, smartEndDate];
    } else {
      // No active predictions: show from 2015 onwards, but default view focused on recent years
      const latestDataDate = timeExtent[1];
      const startOf2015 = new Date('2015-01-01'); // Start from 2015 for more historical data
      const smartStartDate = new Date(Math.max(startOf2015.getTime(), timeExtent[0].getTime()));
      
      // Add future padding to shift current price toward center
      const futureMonths = 18; // Increased from 12 to 18 months for more space
      const futurePadding = futureMonths * 30 * 24 * 60 * 60 * 1000;
      const smartEndDate = new Date(latestDataDate.getTime() + futurePadding);
      
      timeExtent = [smartStartDate, smartEndDate];
    }

    const xScale = d3.scaleTime()
      .domain(timeExtent)
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([yMin, yMax])
      .range([height, 0]);

    // Apply zoom transform to scales
    const zoomedXScale = transform.rescaleX(xScale);
    let zoomedYScale = transform.rescaleY(yScale);
    
    // ✅ CRITICAL FIX: Constrain zoomed Y-axis to never go below -$1000
    const zoomedYDomain = zoomedYScale.domain();
    if (zoomedYDomain[0] < -1000) {
      // If the zoomed domain tries to go below -$1000, clamp it
      const clampedYMin = -1000;
      const clampedYMax = zoomedYDomain[1];
      zoomedYScale = d3.scaleLinear()
        .domain([clampedYMin, clampedYMax])
        .range([height, 0]);
    }

    // Add X axis - always start display from 2019
    const displayTimeExtent = timeExtent;
    const yearSpan = displayTimeExtent[1].getFullYear() - displayTimeExtent[0].getFullYear();
    
    let tickFormat: (date: Date) => string;
    let tickCount: number;
    
    if (yearSpan > 3) {
      tickFormat = d3.timeFormat("'%y");
      tickCount = Math.min(yearSpan, 6);
    } else if (yearSpan > 1) {
      tickFormat = d3.timeFormat("%Y");
      tickCount = yearSpan + 1;
    } else {
      tickFormat = d3.timeFormat("%b");
      tickCount = 6;
    }

    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(zoomedXScale)
        .tickFormat(tickFormat as any)
        .ticks(tickCount))
      .selectAll("text")
      .style("fill", theme.text)
      .style("font-size", "11px")
      .style("font-family", "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif");

    // Add Y axis
    g.append("g")
      .call(d3.axisLeft(zoomedYScale)
        .tickFormat(d => `$${(d as number / 1000).toFixed(0)}k`)
        .ticks(5))
      .selectAll("text")
      .style("fill", theme.text)
      .style("font-size", "11px")
      .style("font-family", "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif");

    // Style axis lines
    g.selectAll(".domain, .tick line")
      .style("stroke", theme.border);

    // Add horizontal grid lines (Y-axis)
    chartArea.selectAll(".y-grid-line")
      .data(zoomedYScale.ticks(6))
      .enter().append("line")
      .attr("class", "y-grid-line")
      .attr("x1", 0)
      .attr("x2", width)
      .attr("y1", d => zoomedYScale(d))
      .attr("y2", d => zoomedYScale(d))
      .style("stroke", theme.border)
      .style("stroke-opacity", 0.4)
      .style("stroke-width", 1);

    // Add vertical grid lines (X-axis)
    chartArea.selectAll(".x-grid-line")
      .data(zoomedXScale.ticks(5))
      .enter().append("line")
      .attr("class", "x-grid-line")
      .attr("x1", d => zoomedXScale(d))
      .attr("x2", d => zoomedXScale(d))
      .attr("y1", 0)
      .attr("y2", height)
      .style("stroke", theme.border)
      .style("stroke-opacity", 0.25)
      .style("stroke-width", 1);

    // Create line generator with zoomed scales
    const line = d3.line<typeof processedData[0]>()
      .x(d => zoomedXScale(d.time))
      .y(d => zoomedYScale(d.value))
      .curve(d3.curveMonotoneX);

    // Add the main price line (clipped)
    chartArea.append("path")
      .datum(processedData)
      .attr("fill", "none")
      .attr("stroke", theme.primary)
      .attr("stroke-width", 2.5)
      .attr("d", line);

    // Add watermark to the chart
    svg.append('g')
      .attr('class', 'chart-watermark')
      .attr('transform', `translate(${width / 2 + margin.left}, ${height / 2 + margin.top})`)
      .style('pointer-events', 'none') // Make sure it doesn't capture touch events
      .call(g => {
        // Add "proofofprice.com" watermark text
        g.append('text')
          .attr('text-anchor', 'middle')
          .attr('font-size', '20px')
          .attr('font-weight', 'bold')
          .attr('font-family', 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif')
          .attr('fill', theme.text)
          .attr('opacity', 0.06)
          .attr('transform', 'rotate(-15)')
          .text('proofofprice.com');
      });

    // Add current price indicator (clipped)
    const latestPoint = processedData[processedData.length - 1];
    if (latestPoint) {
      const currentX = zoomedXScale(latestPoint.time);
      const currentY = zoomedYScale(latestPoint.value);

      // Only show if within visible area
      if (currentX >= 0 && currentX <= width) {
        // Determine price change direction and color
        const previousPoint = processedData[processedData.length - 2];
        let priceChangeColor = theme.primary;
        let priceChangeSymbol = '';
        
        if (previousPoint) {
          const priceDiff = latestPoint.value - previousPoint.value;
          if (priceDiff > 0) {
            priceChangeColor = '#4CAF50'; // Green for up
            priceChangeSymbol = '↗';
          } else if (priceDiff < 0) {
            priceChangeColor = '#F44336'; // Red for down
            priceChangeSymbol = '↘';
          }
        }

        // Add subtle pulse animation ring
        chartArea.append("circle")
          .attr("cx", currentX)
          .attr("cy", currentY)
          .attr("r", 6)
          .attr("fill", "none")
          .attr("stroke", priceChangeColor)
          .attr("stroke-width", 1)
          .attr("stroke-opacity", 0.6)
          .transition()
          .duration(2000)
          .ease(d3.easeLinear)
          .attr("r", 12)
          .attr("stroke-opacity", 0)
          .on("end", function() {
            // Restart the pulse animation
            d3.select(this)
              .attr("r", 6)
              .attr("stroke-opacity", 0.6)
              .transition()
              .duration(2000)
              .ease(d3.easeLinear)
              .attr("r", 12)
              .attr("stroke-opacity", 0);
          });

        // Current price dot with color coding
        chartArea.append("circle")
          .attr("cx", currentX)
          .attr("cy", currentY)
          .attr("r", 4)
          .attr("fill", priceChangeColor)
          .attr("stroke", theme.surface)
          .attr("stroke-width", 2);

        // Current price label with directional arrow (on left side)
        addLabelWithBackground(
          chartArea,
          currentX - 8,
          currentY - 8,
          `${priceChangeSymbol} $${Math.round(latestPoint.value).toLocaleString()}`,
          priceChangeColor,
          'end',
          undefined,
          '14px',
          '700'
        );
      }
    }

    // SIMPLE PREDICTION RENDERING: Show only active predictions
    activePredictions.forEach(predictionId => {
      const prediction = predictions.find(p => p.id === predictionId);
      if (!prediction) return;

      const predictionType = prediction.prediction_type || 'target';
      
      if (predictionType === 'target') {
        // TARGET PREDICTIONS: Dots with status colors
        let predictionPrice: number | null = null;
        const priceValue = prediction.price_target ?? prediction.price_min ?? prediction.price_prediction;
        predictionPrice = typeof priceValue === 'string' ? parseFloat(priceValue) : (priceValue ?? null);
        
        // Debug logging for Peter Zeihan's prediction
        if (prediction.influencer?.name?.toLowerCase().includes('zeihan')) {
          console.log('Zeihan prediction price debug:', {
            id: prediction.id,
            priceValue: priceValue,
            predictionPrice: predictionPrice,
            isFinite: predictionPrice !== null ? isFinite(predictionPrice) : false,
            yScale: predictionPrice !== null ? yScale(predictionPrice) : 'N/A',
            target_time: prediction.target_time
          });
        }
        
        const parseTime = d3.timeParse('%Y-%m-%d');
        let predictionDate: Date | null = null;
        if (prediction.target_time) {
          predictionDate = parseTime(prediction.target_time) || new Date(prediction.target_time);
        }
        if (!predictionDate || isNaN(predictionDate.getTime())) {
          predictionDate = new Date();
        }
        
        if (predictionPrice !== null && predictionPrice !== undefined && isFinite(predictionPrice) && predictionDate) {
          const predX = zoomedXScale(predictionDate);
          const predY = zoomedYScale(predictionPrice);
          
          console.log('Prediction positioning debug:', {
            id: predictionId,
            price: predictionPrice,
            x: predX,
            y: predY,
            expertName: prediction.influencer?.name
          });
          
          // Status-based prediction dot
          chartArea.append("circle")
            .attr("cx", predX)
            .attr("cy", predY)
            .attr("r", 4)
            .attr("fill", getStatusColor(prediction, true))
            .attr("stroke", theme.surface)
            .attr("stroke-width", 2)
            .style("opacity", 0.9)
            .style("cursor", "pointer")
            .on("click", function(event) {
              event.stopPropagation();
              resetZoom();
            });
          
          // Status-based prediction label - POSITIONED TO THE LEFT
          addLabelWithBackground(
            chartArea,
            predX - 8,
            predY - 8,
            formatChartPrice(predictionPrice),
            getStatusColor(prediction, true),
            'end',
            resetZoom
          );
          
          // Add status indicator below the price
          const statusText = getStatusText(prediction);
          addLabelWithBackground(
            chartArea,
            predX - 8,
            predY + 10,
            statusText,
            getStatusColor(prediction, true),
            'end',
            resetZoom,
            '10px',
            '500'
          );
        }
      }
      
      else if (predictionType === 'range') {
        // RANGE PREDICTIONS: Rectangular boxes with status colors
        const startDate = new Date(prediction.start_date || new Date());
        const endDate = new Date(prediction.end_date || prediction.target_time || new Date());
        // ✅ FIX: Convert price strings to numbers and handle zero values
        const priceMin = typeof prediction.price_min === 'string' 
          ? parseFloat(prediction.price_min) 
          : (prediction.price_min ?? 0);
        const priceMax = typeof prediction.price_max === 'string' 
          ? parseFloat(prediction.price_max) 
          : (prediction.price_max ?? 0);
        
        if (priceMin !== null && priceMax !== null && isFinite(priceMin) && isFinite(priceMax) && priceMax > priceMin) {
          const x1 = zoomedXScale(startDate);
          const x2 = zoomedXScale(endDate);
          const y1 = zoomedYScale(priceMax); // Top of box
          const y2 = zoomedYScale(priceMin); // Bottom of box
          
          const isSuccess = prediction.success_status === 'success';
          
          // Status-based transparent rectangle with green glow for success
          chartArea.append("rect")
            .attr("x", Math.min(x1, x2))
            .attr("y", y1)
            .attr("width", Math.abs(x2 - x1))
            .attr("height", Math.abs(y2 - y1))
            .attr("fill", isSuccess ? 'rgba(76, 175, 80, 0.25)' : getStatusColor(prediction))
            .attr("fill-opacity", isSuccess ? 1 : 0.1)
            .attr("stroke", getStatusColor(prediction, true))
            .attr("stroke-width", isSuccess ? 2 : 1)
            .attr("stroke-opacity", isSuccess ? 0.9 : 0.6)
            .attr("rx", 2) // Rounded corners
            .attr("filter", isSuccess ? "url(#success-glow-mobile)" : null)
            .style("cursor", "pointer")
            .on("click", function(event) {
              event.stopPropagation();
              resetZoom();
            });
          
          // Status-based label - POSITIONED TO THE LEFT OF BOX
          const leftX = Math.min(x1, x2);
          const centerY = (y1 + y2) / 2;
          
          addLabelWithBackground(
            chartArea,
            leftX - 8,
            centerY - 8,
            `${formatChartPrice(priceMin)}-${formatChartPrice(priceMax)}`,
            getStatusColor(prediction, true),
            'end',
            resetZoom
          );
          
          // Add status indicator for range predictions
          const statusText = getStatusText(prediction);
          addLabelWithBackground(
            chartArea,
            leftX - 8,
            centerY + 8,
            statusText,
            getStatusColor(prediction, true),
            'end',
            resetZoom,
            '10px',
            '500'
          );
        }
      }
      
      else if (predictionType === 'pricetimerange') {
        // PRICE-TIME RANGE: Horizontal dashed line with status colors
        console.log('Price-time range debug:', {
          start_date: prediction.start_date,
          end_date: prediction.end_date,
          target_time: prediction.target_time,
          price_min: prediction.price_min,
          status: prediction.success_status
        });

        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        const endDate = prediction.end_date ? new Date(prediction.end_date) : null;
        // ✅ FIX: Convert price_min string to number and handle zero values
        const targetPrice = typeof prediction.price_min === 'string' 
          ? parseFloat(prediction.price_min) 
          : (prediction.price_min ?? 0);
        
        if (!startDate || !endDate || targetPrice === null || targetPrice === undefined || !isFinite(targetPrice)) {
          console.warn(`⚠️ Invalid pricetimerange data for prediction ${predictionId}`);
          return;
        }

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          console.error(`❌ Invalid dates for prediction ${predictionId}`);
          return;
        }

        const x1 = zoomedXScale(startDate);
        const x2 = zoomedXScale(endDate);
        const y = zoomedYScale(targetPrice);
        
        
        // Status-based horizontal dashed line
        chartArea.append("line")
          .attr("x1", Math.min(x1, x2))
          .attr("x2", Math.max(x1, x2))
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", getStatusColor(prediction, true))
          .attr("stroke-width", 2)
          .attr("stroke-dasharray", "4,3")
          .attr("stroke-opacity", 0.8)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
        
        // Status-based label - POSITIONED TO THE LEFT OF LINE
        addLabelWithBackground(
          chartArea,
          Math.min(x1, x2) - 8,
          y - 8,
          formatChartPrice(targetPrice),
          getStatusColor(prediction, true),
          'end',
          resetZoom
        );
        
        // Add status indicator for pricetimerange predictions
        const statusText = getStatusText(prediction);
        addLabelWithBackground(
          chartArea,
          Math.min(x1, x2) - 8,
          y + 10,
          statusText,
          getStatusColor(prediction, true),
          'end',
          resetZoom,
          '10px',
          '500'
        );
      }
      
      else if (predictionType === 'top') {
        // TOP PREDICTIONS: Solid horizontal lines starting from start_date
        console.log('Top prediction debug:', {
          start_date: prediction.start_date,
          price_max: prediction.price_max,
          price_target: prediction.price_target,
          status: prediction.success_status
        });

        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        // For TOP predictions, use price_max or fallback to price_target
        const topPrice = typeof prediction.price_max === 'string' 
          ? parseFloat(prediction.price_max) 
          : (prediction.price_max ?? prediction.price_target ?? 0);
        
        if (!startDate || topPrice === null || topPrice === undefined || !isFinite(topPrice)) {
          console.warn(`⚠️ Invalid TOP data for prediction ${predictionId}`);
          return;
        }

        if (isNaN(startDate.getTime())) {
          console.error(`❌ Invalid start date for TOP prediction ${predictionId}`);
          return;
        }

        // Line goes from start_date to end_date or Dec 31st, but constrained to visible area
        const [xMin, xMax] = zoomedXScale.domain();
        const endDate = prediction.end_date 
          ? new Date(prediction.end_date) 
          : new Date(new Date().getFullYear(), 11, 31); // Dec 31st of current year as fallback
        
        const x1 = zoomedXScale(startDate);
        const calculatedX2 = zoomedXScale(endDate);
        
        // CRITICAL FIX: Don't let TOP lines extend beyond reasonable viewport
        // If the calculated end point is beyond the current viewport, constrain it
        const viewportWidth = zoomedXScale.range()[1] - zoomedXScale.range()[0];
        const maxReasonableX = zoomedXScale.range()[1]; // Right edge of chart
        const x2 = Math.min(calculatedX2, maxReasonableX);
        
        console.log('TOP prediction line calculation:', {
          startDate: startDate,
          endDate: endDate,
          x1: x1,
          calculatedX2: calculatedX2,
          constrainedX2: x2,
          viewportWidth: viewportWidth,
          prediction_id: prediction.id
        });
        
        const y = zoomedYScale(topPrice);
        
        
        // Status-based solid horizontal line
        chartArea.append("line")
          .attr("x1", Math.max(x1, zoomedXScale(xMin))) // Don't start before visible area
          .attr("x2", x2)
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", getStatusColor(prediction, true))
          .attr("stroke-width", 2)
          .attr("stroke-opacity", 0.8)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
        
        // Status-based label - POSITIONED INSIDE THE CHART (not at the edge)
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y - 8,
          `TOP ${formatChartPrice(topPrice)}`,
          getStatusColor(prediction, true),
          'end',
          resetZoom
        );
        
        // Add status indicator for TOP predictions
        const statusText = getStatusText(prediction);
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y + 10,
          statusText,
          getStatusColor(prediction, true),
          'end',
          resetZoom,
          '10px',
          '500'
        );
      }
      
      else if (predictionType === 'bottom') {
        // BOTTOM PREDICTIONS: Solid horizontal lines starting from start_date
        console.log('Bottom prediction debug:', {
          start_date: prediction.start_date,
          price_min: prediction.price_min,
          price_target: prediction.price_target,
          status: prediction.success_status
        });

        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        // For BOTTOM predictions, use price_min or fallback to price_target
        const bottomPrice = typeof prediction.price_min === 'string' 
          ? parseFloat(prediction.price_min) 
          : (prediction.price_min ?? prediction.price_target ?? 0);
        
        if (!startDate || bottomPrice === null || bottomPrice === undefined || !isFinite(bottomPrice)) {
          console.warn(`⚠️ Invalid BOTTOM data for prediction ${predictionId}`);
          return;
        }

        if (isNaN(startDate.getTime())) {
          console.error(`❌ Invalid start date for BOTTOM prediction ${predictionId}`);
          return;
        }

        // Line goes from start_date to the end of the visible chart
        const [xMin, xMax] = zoomedXScale.domain();
        const x1 = zoomedXScale(startDate);
        const x2 = zoomedXScale(xMax); // End of visible chart
        const y = zoomedYScale(bottomPrice);
        
        
        // Status-based solid horizontal line
        chartArea.append("line")
          .attr("x1", Math.max(x1, zoomedXScale(xMin))) // Don't start before visible area
          .attr("x2", x2)
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", getStatusColor(prediction, true))
          .attr("stroke-width", 2)
          .attr("stroke-opacity", 0.8)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
        
        // Status-based label - POSITIONED INSIDE THE CHART (not at the edge)
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y - 8,
          `BOTTOM ${formatChartPrice(bottomPrice)}`,
          getStatusColor(prediction, true),
          'end',
          resetZoom
        );
        
        // Add status indicator for BOTTOM predictions (positioned below the line)
        const statusText = getStatusText(prediction);
        addLabelWithBackground(
          chartArea,
          x2 - 20,
          y + 10,
          statusText,
          getStatusColor(prediction, true),
          'end',
          resetZoom,
          '10px',
          '500'
        );
      }
      
      else if (predictionType === 'bet') {
        // BET PREDICTIONS: Dice icon with participants and price target (no tweet cards on mobile)
        console.log('Bet prediction debug:', {
          id: prediction.id,
          influencer: prediction.influencer?.name,
          price_prediction: prediction.price_prediction,
          start_date: prediction.start_date,
          end_date: prediction.end_date,
          status: prediction.success_status
        });

        const betAmount = prediction.price_prediction || prediction.price_target || prediction.price_min;
        const startDate = prediction.start_date ? new Date(prediction.start_date) : null;
        const endDate = prediction.end_date ? new Date(prediction.end_date) : null;
        
        if (!betAmount || !endDate || isNaN(endDate.getTime())) {
          console.warn(`⚠️ Invalid BET data for prediction ${predictionId}`);
          return;
        }

        const betPrice = typeof betAmount === 'string' ? parseFloat(betAmount) : betAmount;
        if (!isFinite(betPrice)) {
          console.warn(`⚠️ Invalid bet price for prediction ${predictionId}:`, betAmount);
          return;
        }

        // Find ALL participants in this bet wager (same price + date)
        const betParticipants = activePredictions
          .map(id => predictions.find(p => p.id === id))
          .filter(p => p && 
            p.prediction_type === 'bet' && 
            (p.price_prediction || p.price_target || p.price_min) === betAmount &&
            p.end_date === prediction.end_date
          );

        // Only render once per bet wager (for the first participant found)
        const isFirstParticipant = betParticipants[0]?.id === predictionId;
        if (!isFirstParticipant) {
          return; // Skip rendering for other participants of the same bet
        }

        console.log('🎲 Rendering bet wager with participants:', betParticipants.map(p => p?.influencer?.name));

        const x1 = startDate ? zoomedXScale(startDate) : zoomedXScale(endDate) - 50;
        const x2 = zoomedXScale(endDate);
        const y = zoomedYScale(betPrice);
        
        // BET LINE: Orange/yellow line representing the wager timeframe
        chartArea.append("line")
          .attr("x1", x1)
          .attr("x2", x2)
          .attr("y1", y)
          .attr("y2", y)
          .attr("stroke", "#F7931A") // Bitcoin orange
          .attr("stroke-width", 3)
          .attr("stroke-opacity", 0.8)
          .attr("stroke-linecap", "round")
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });

        // DICE MARKER at start of bet
        const diceGroup = chartArea.append("g").attr("class", "bet-dice-marker");
        
        diceGroup.append("circle")
          .attr("cx", x1)
          .attr("cy", y)
          .attr("r", 8)
          .attr("fill", getStatusColor(prediction, true))
          .attr("stroke", theme.surface)
          .attr("stroke-width", 2)
          .style("cursor", "pointer")
          .on("click", function(event) {
            event.stopPropagation();
            resetZoom();
          });
          
        diceGroup.append("text")
          .attr("x", x1)
          .attr("y", y + 3)
          .attr("text-anchor", "middle")
          .attr("font-size", "10px")
          .attr("fill", theme.surface)
          .text("🎲");

        // PARTICIPANT NAMES: Stacked on the left side with collision detection
        if (betParticipants.length >= 2) {
          const leftX = x1 - 15; // Position to the left of the bet line
          const baseY = y - 20;   // Start above the bet line
          
          // Sort participants: winner first, then others
          const sortedParticipants = [...betParticipants].sort((a, b) => {
            if (a!.success_status === 'success') return -1;
            if (b!.success_status === 'success') return 1;
            return 0;
          });
          
          // Prepare all labels (participants + "vs") for collision detection
          const allLabelData: Array<{x: number, y: number, height: number, text: string, color: string, id: string, type: string, participant?: any}> = [];
          
          // Add participant labels
          sortedParticipants.forEach((participant, index) => {
            if (!participant) return;
            
            const participantName = participant.influencer?.name || 'Unknown';
            const statusSymbol = participant.success_status === 'success' ? ' ✓' : 
                                participant.success_status === 'miss' ? ' ✗' : '';
            
            allLabelData.push({
              x: leftX,
              y: baseY + (index * 25), // Start with more spacing
              height: 16,
              text: participantName + statusSymbol,
              color: getStatusColor(participant, true),
              id: `participant-${participant.id}`,
              type: 'participant',
              participant: participant
            });
            
            // Add "vs" label between participants (except after the last one)
            if (index < sortedParticipants.length - 1) {
              allLabelData.push({
                x: leftX,
                y: baseY + (index * 25) + 12, // Position between participants
                height: 12,
                text: 'vs',
                color: '#888888',
                id: `vs-${index}`,
                type: 'vs'
              });
            }
          });
          
          // Resolve collisions for all labels together
          const resolvedLabels = resolveCollisions(allLabelData);
          
          // Render all resolved labels
          resolvedLabels.forEach((labelInfo) => {
            const fontSize = labelInfo.type === 'vs' ? '10px' : '12px';
            const fontWeight = labelInfo.type === 'vs' ? '400' : '600';
            
            addLabelWithBackground(
              chartArea,
              labelInfo.x,
              labelInfo.y,
              labelInfo.text,
              labelInfo.color,
              'end',
              resetZoom,
              fontSize,
              fontWeight
            );
          });
        } else {
          // Single participant (fallback)
          const participantName = prediction.influencer?.name || 'Unknown';
          addLabelWithBackground(
            chartArea,
            x1 - 15,
            y - 8,
            participantName,
            getStatusColor(prediction, true),
            'end',
            resetZoom,
            '12px',
            '600'
          );
        }

        // BET AMOUNT LABEL: Show on the right side  
        addLabelWithBackground(
          chartArea,
          x2 + 8,
          y - 8,
          `🎲 ${formatChartPrice(betPrice)}`,
          getStatusColor(prediction, true),
          'start',
          resetZoom
        );
        
        // Add status indicator for bet predictions (show overall bet status)
        const overallStatus: 'success' | 'miss' | 'updated' | 'pending' = 
          betParticipants.some(p => p?.success_status === 'success') ? 'success' :
          betParticipants.some(p => p?.success_status === 'miss') ? 'miss' :
          betParticipants.some(p => p?.success_status === 'updated') ? 'updated' : 'pending';
        
        const statusPrediction = { ...prediction, success_status: overallStatus };
        const statusText = getStatusText(statusPrediction);
        
        addLabelWithBackground(
          chartArea,
          x2 + 8,
          y + 10,
          statusText,
          getStatusColor(statusPrediction, true),
          'start',
          resetZoom,
          '10px',
          '500'
        );
      }
      // SENTIMENT PREDICTIONS: Dot at current BTC price when comment was made (displayed as "talk" in UI)
      else if (predictionType === 'sentiment') {
        // For sentiment tweets, use target_time first (like desktop), then fallback to prediction_made or created_at
        const sentimentDate = prediction.target_time
          ? new Date(prediction.target_time)
          : (prediction.prediction_made
              ? new Date(prediction.prediction_made)
              : (prediction.created_at ? new Date(prediction.created_at) : null));
        
        console.log('Processing sentiment prediction:', {
          id: prediction.id,
          expert: prediction.influencer?.name,
          target_time: prediction.target_time,
          prediction_made: prediction.prediction_made,
          created_at: prediction.created_at,
          sentimentDate: sentimentDate
        });
        
        if (sentimentDate && !isNaN(sentimentDate.getTime())) {
          // For sentiment predictions, find the Bitcoin price at the time the comment was made
          let bitcoinPriceAtTime: number | null = null;
          if (chartData && chartData.length > 0) {
            // Find the closest price data point to the sentiment date
            const sentimentTime = sentimentDate.getTime();
            let closestPoint: { time: string; value: number } | null = null;
            let minTimeDiff = Infinity;
            
            for (const dataPoint of chartData) {
              const dataTime = new Date(dataPoint.time).getTime();
              const timeDiff = Math.abs(dataTime - sentimentTime);
              if (timeDiff < minTimeDiff) {
                minTimeDiff = timeDiff;
                closestPoint = dataPoint;
              }
            }
            
            // For historical sentiment tweets (like Saylor 2013), be more lenient with time matching
            // If we have the closest point, use it regardless of time difference for historical tweets
            if (closestPoint) {
              const sentimentYear = sentimentDate.getFullYear();
              const currentYear = new Date().getFullYear();
              const isHistoricalSentiment = (currentYear - sentimentYear) > 2;
              
              if (isHistoricalSentiment) {
                // For historical tweets, use the closest available price regardless of time gap
                bitcoinPriceAtTime = closestPoint.value;
                console.log('Historical sentiment - using closest price:', {
                  sentimentYear,
                  currentYear,
                  timeDiffDays: minTimeDiff / (24 * 60 * 60 * 1000),
                  price: bitcoinPriceAtTime
                });
              } else {
                // For recent sentiment tweets, keep the 7-day limit
                if (minTimeDiff < 7 * 24 * 60 * 60 * 1000) {
                  bitcoinPriceAtTime = closestPoint.value;
                }
              }
            }
          }
          
          // Fallback price for very historical sentiment tweets when no chart data is available
          if (!bitcoinPriceAtTime && sentimentDate.getFullYear() <= 2014) {
            bitcoinPriceAtTime = 600; // Approximate BTC price in 2013-2014 era
            console.log('Using fallback price for historical sentiment:', bitcoinPriceAtTime);
          }
          
          if (bitcoinPriceAtTime && isFinite(bitcoinPriceAtTime)) {
            const x = zoomedXScale(sentimentDate);
            const y = zoomedYScale(bitcoinPriceAtTime);
            
            // Add a larger circle for sentiment predictions
            chartArea.append('circle')
              .attr('cx', x)
              .attr('cy', y)
              .attr('r', 6)
              .attr('fill', '#64B5F6')
              .attr('stroke', theme.surface)
              .attr('stroke-width', 2)
              .attr('opacity', 0.9);
            
            // Add a speech bubble emoji next to the dot
            chartArea.append('text')
              .attr('x', x + 10)
              .attr('y', y - 10)
              .attr('fill', '#64B5F6')
              .attr('font-size', '14px')
              .attr('font-weight', 'bold')
              .style('text-anchor', 'start')
              .text('💬');
            
            console.log('Rendered sentiment prediction:', {
              id: prediction.id,
              expert: prediction.influencer?.name,
              date: sentimentDate,
              btcPrice: bitcoinPriceAtTime,
              x, y
            });
          }
        }
      }
    });

    // ADD SUCCESS CROSSING POINTS: Show green dots where successful predictions were reached
    const successfulPredictions = activePredictions
      .map(id => predictions.find(p => p.id === id))
      .filter(p => p && p.success_status === 'success');

    successfulPredictions.forEach(prediction => {
      if (!prediction) return;
      
      
      // Find where the price actually crossed the prediction target
      const crossingPoint = findCrossingPoint(processedData, prediction);
      
      if (crossingPoint) {
        const crossX = zoomedXScale(crossingPoint.time);
        const crossY = zoomedYScale(crossingPoint.value);
        
        
        // Large green success dot
        chartArea.append("circle")
          .attr("cx", crossX)
          .attr("cy", crossY)
          .attr("r", 6) // Larger than regular prediction dots
          .attr("fill", predictionStyles.colors.successSolid)
          .attr("stroke", "white")
          .attr("stroke-width", 2)
          .style("opacity", 1);
        
        // Add a subtle pulse ring around the success dot
        chartArea.append("circle")
          .attr("cx", crossX)
          .attr("cy", crossY)
          .attr("r", 8)
          .attr("fill", "none")
          .attr("stroke", predictionStyles.colors.successSolid)
          .attr("stroke-width", 1)
          .attr("stroke-opacity", 0.3);
        
        // Success label showing when target was reached
        addLabelWithBackground(
          chartArea,
          crossX - 8,
          crossY - 12,
          `✓ ${crossingPoint.time.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
          predictionStyles.colors.successSolid,
          'end',
          undefined,
          '11px',
          '600'
        );
      } else {
      }
    });

    // Render user's contest prediction (if they have one)
    if (userContestPrediction) {
      const yPos = zoomedYScale(userContestPrediction);
      const yDomain = zoomedYScale.domain();
      
      // Contest target date: April 28, 2026 (Bitcoin Conference 2026)
      const targetDate = new Date('2026-04-28');
      const targetX = zoomedXScale(targetDate);
      const xDomain = zoomedXScale.domain();
      
      // Only render if price is in visible range
      if (userContestPrediction >= yDomain[0] && userContestPrediction <= yDomain[1]) {
        // Check if target date is in visible range
        const isTargetVisible = targetDate >= xDomain[0] && targetDate <= xDomain[1];
        const lineEndX = isTargetVisible ? targetX : width;
        
        // Add CSS animations if not already present
        if (!document.getElementById('contest-prediction-animations')) {
          const style = document.createElement('style');
          style.id = 'contest-prediction-animations';
          style.textContent = `
            @keyframes userTargetRing {
              0% { r: 8; opacity: 1; }
              100% { r: 30; opacity: 0; }
            }
            @keyframes userTargetPulse {
              0%, 100% { r: 8; opacity: 1; }
              50% { r: 10; opacity: 0.8; }
            }
          `;
          document.head.appendChild(style);
        }
        
        // Create group for user prediction
        const userGroup = chartArea.append('g')
          .attr('class', 'user-contest-prediction');
        
        // Add defs for glow effect
        let defs = chartArea.select<SVGDefsElement>('defs.user-glow-defs');
        if (defs.empty()) {
          defs = chartArea.append('defs').attr('class', 'user-glow-defs');
          const filter = defs.append('filter')
            .attr('id', 'mobile-user-glow')
            .attr('x', '-100%')
            .attr('y', '-100%')
            .attr('width', '300%')
            .attr('height', '300%');
          filter.append('feGaussianBlur')
            .attr('stdDeviation', '3')
            .attr('result', 'coloredBlur');
          const feMerge = filter.append('feMerge');
          feMerge.append('feMergeNode').attr('in', 'coloredBlur');
          feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
        }
        
        // Horizontal line to target date (not full width)
        userGroup.append('line')
          .attr('x1', 0)
          .attr('x2', lineEndX)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', '#4caf50')
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '6,4')
          .attr('opacity', 0.7);
        
        // If target date is visible, show vertical marker and target point
        if (isTargetVisible && targetX > 0 && targetX < width) {
          // Vertical line at target date
          userGroup.append('line')
            .attr('x1', targetX)
            .attr('x2', targetX)
            .attr('y1', yPos - 30)
            .attr('y2', yPos + 30)
            .attr('stroke', '#4caf50')
            .attr('stroke-width', 2)
            .attr('opacity', 0.5);
          
          // Expanding ring animation at target point
          userGroup.append('circle')
            .attr('class', 'user-target-ring')
            .attr('cx', targetX)
            .attr('cy', yPos)
            .attr('r', 8)
            .attr('fill', 'none')
            .attr('stroke', '#4caf50')
            .attr('stroke-width', 2)
            .style('animation', 'userTargetRing 2s ease-out infinite');
          
          // Second ring (offset timing)
          userGroup.append('circle')
            .attr('class', 'user-target-ring-2')
            .attr('cx', targetX)
            .attr('cy', yPos)
            .attr('r', 8)
            .attr('fill', 'none')
            .attr('stroke', '#4caf50')
            .attr('stroke-width', 2)
            .style('animation', 'userTargetRing 2s ease-out infinite')
            .style('animation-delay', '1s');
          
          // Main pulsating target dot with glow
          userGroup.append('circle')
            .attr('class', 'user-target-dot')
            .attr('cx', targetX)
            .attr('cy', yPos)
            .attr('r', 8)
            .attr('fill', '#4caf50')
            .style('filter', 'url(#mobile-user-glow)')
            .style('animation', 'userTargetPulse 2s ease-in-out infinite');
          
          // Inner dot (static white center)
          userGroup.append('circle')
            .attr('class', 'user-target-center')
            .attr('cx', targetX)
            .attr('cy', yPos)
            .attr('r', 3)
            .attr('fill', '#fff');
          
          // Target date label
          addLabelWithBackground(
            chartArea,
            targetX,
            yPos - 35,
            '🎯 Apr 28',
            '#4caf50',
            'middle',
            undefined,
            '9px',
            '600'
          );
          
          // Price label (offset to the left)
          addLabelWithBackground(
            chartArea,
            targetX - 20,
            yPos + 20,
            `$${userContestPrediction.toLocaleString()}`,
            '#4caf50',
            'end',
            undefined,
            '11px',
            '700'
          );
        } else {
          // Target date not visible - show indicator at right edge pointing toward future
          const edgeX = width - 20;
          
          // Arrow pointing right (toward future)
          userGroup.append('path')
            .attr('d', `M ${edgeX - 10} ${yPos - 6} L ${edgeX} ${yPos} L ${edgeX - 10} ${yPos + 6}`)
            .attr('fill', 'none')
            .attr('stroke', '#4caf50')
            .attr('stroke-width', 2);
          
          // Pulsating dot at edge
          userGroup.append('circle')
            .attr('class', 'user-target-dot')
            .attr('cx', edgeX)
            .attr('cy', yPos)
            .attr('r', 6)
            .attr('fill', '#4caf50')
            .style('filter', 'url(#mobile-user-glow)')
            .style('animation', 'userTargetPulse 2s ease-in-out infinite');
          
          // Label with price
          addLabelWithBackground(
            chartArea,
            edgeX - 15,
            yPos - 15,
            `🎯 $${userContestPrediction.toLocaleString()}`,
            '#4caf50',
            'end',
            undefined,
            '10px',
            '700'
          );
        }
      }
    }

  }, [chartData, predictions, activePredictions, isDarkMode, theme, resetZoom, userContestPrediction, userHandle]);

  // Initialize zoom behavior and redraw chart
  useEffect(() => {
    if (!svgRef.current) return;

    // Create zoom behavior with Y-axis constraints
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 50]) // Allow zoom out to 50% and zoom in to 5000% for detailed analysis
      .translateExtent([[-800, -200], [Infinity, 800]]) // Allow more leftward panning to reach 2015 data
      .on('zoom', (event) => {
        const transform = event.transform;
        // Prevent horizontal panning to the left (going backwards in time)
        if (transform.x > 0) {
          transform.x = 0;
        }
        transformRef.current = transform;
        redrawChart(transform);
      });

    // Apply zoom to SVG
    const svg = d3.select(svgRef.current);
    svg.call(zoom);
    zoomRef.current = zoom;

    // Initial draw
    redrawChart(transformRef.current);

    // Cleanup
    return () => {
      svg.on('.zoom', null);
    };
  }, [redrawChart]);

  // Reset zoom when all predictions are cleared (reset button clicked)
  useEffect(() => {
    if (activePredictions.length === 0) {
      resetZoom();
    }
  }, [activePredictions.length, resetZoom]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '280px' }}>
      <svg
        ref={svgRef}
        width="100%"
        height={280} // Reduced from 400px to match new ChartContainer height
        style={{ 
          background: 'transparent', 
          display: 'block',
          // Safari compatibility improvements
          WebkitTapHighlightColor: 'transparent',
          WebkitTouchCallout: 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          // Enable pinch-to-zoom and pan gestures
          touchAction: 'none' // Changed from 'pan-x pan-y' to 'none' to allow D3 zoom to handle all touch events
          // Prevent rubber band scrolling on iOS Safari
          // overscrollBehavior: 'contain',
          // Cursor styles for desktop
          // cursor: 'grab'
        }}
      />

      {/* 🐰 EASTER EGG COUNTER (Mobile) */}
      {easterEggCollected > 0 && (
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(0,0,0,0.8)',
          color: '#F7931A',
          padding: '10px 14px',
          borderRadius: '18px',
          fontSize: '14px',
          fontWeight: 'bold',
          zIndex: 998,
          border: '2px solid #F7931A',
          boxShadow: '0 4px 12px rgba(247, 147, 26, 0.3)',
        }}>
          🥚 {easterEggCollected} / 21
        </div>
      )}

      {/* 🐰 BITCOIN EXPLOSIONS (Mobile) */}
      {bitcoinExplosions.map((explosion) => (
        <div
          key={explosion.id}
          style={{
            position: 'absolute',
            left: explosion.x + 'px',
            top: explosion.y + 'px',
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#F7931A',
            zIndex: 1000,
            transform: 'translate(-50%, -50%)',
            animation: 'bitcoinExplosion 2s ease-out forwards',
            pointerEvents: 'none',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
          }}
        >
          ₿
        </div>
      ))}

      {/* 🐰 EASTER EGGS RENDERING (Mobile) - BIGGER TAP TARGETS */}
      {easterEggs.filter(egg => !egg.collected).map((egg) => (
        <div
          key={egg.id}
          onTouchStart={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleEggClick(egg.id);
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleEggClick(egg.id);
          }}
          style={{
            position: 'absolute',
            left: egg.x + 'px',
            top: egg.y + 'px',
            width: '50px', // Much bigger tap target!
            height: '50px', // Much bigger tap target!
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 999,
            transform: 'translate(-50%, -50%)',
            // Invisible tap area - just for easier touching
            background: 'rgba(0,0,0,0)', // Transparent
          }}
        >
          {/* Visual egg inside the larger tap area */}
          <div
            style={{
              width: '24px', // Visual egg size
              height: '30px',
              background: egg.color,
              borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
              boxShadow: '0 3px 8px rgba(0,0,0,0.4)',
              border: '3px solid rgba(255,255,255,0.9)',
              animation: 'bounce 1.2s ease-in-out infinite alternate',
              pointerEvents: 'none', // Let parent handle touches
            }}
          />
        </div>
      ))}

      {/* 🐰 BITCOIN EXPLOSIONS (Mobile) */}
      {bitcoinExplosions.map((explosion) => (
        <div
          key={explosion.id}
          style={{
            position: 'absolute',
            left: explosion.x + 'px',
            top: explosion.y + 'px',
            transform: 'translate(-50%, -50%)',
            fontSize: '24px',
            zIndex: 1000,
            animation: 'bitcoinExplosion 2s ease-out forwards',
            pointerEvents: 'none',
          }}
        >
          ₿
        </div>
      ))}

      {/* 🐰 EASTER EGG PROGRESS COUNTER (Mobile) */}
      {(isEasterEggHuntActive || easterEggCollected > 0) && easterEggCollected < 21 && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: 'rgba(0, 0, 0, 0.8)',
          borderRadius: '12px',
          padding: '8px 12px',
          zIndex: 9999,
          border: '2px solid #FFD700',
          backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            color: 'white',
            fontSize: '16px',
            fontWeight: 'bold',
            marginBottom: '4px',
            textAlign: 'center',
            animation: easterEggCollected > 0 ? 'counterPulse 0.6s ease-out' : 'none',
          }}>
            🥚 {easterEggCollected}/21
          </div>
          <div style={{
            width: '80px',
            height: '6px',
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '3px',
            overflow: 'hidden',
            marginBottom: '6px',
          }}>
            <div style={{
              width: `${(easterEggCollected / 21) * 100}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #FFD700, #FFA500)',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
            }} />
          </div>
          {/* Reset button if no eggs are falling */}
          {!easterEggs.some(egg => !egg.collected) && easterEggCollected < 21 && (
            <button
              onClick={() => {
                console.log('🐰 Manual reset triggered');
                setIsEasterEggHuntActive(false);
                setEasterEggs([]);
                setEasterEggCollected(0);
                easterEggSpawnedRef.current = false;
              }}
              style={{
                background: '#FFD700',
                color: '#000',
                border: 'none',
                borderRadius: '4px',
                padding: '2px 6px',
                fontSize: '10px',
                fontWeight: 'bold',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              🔄 Restart
            </button>
          )}
        </div>
      )}

      {/* 🐰 EASTER EGG CSS ANIMATIONS */}
      <style>
        {`
          @keyframes bitcoinExplosion {
            0% {
              transform: translate(-50%, -50%) scale(0.5);
              opacity: 1;
            }
            50% {
              transform: translate(-50%, -50%) scale(1.5);
              opacity: 0.8;
            }
            100% {
              transform: translate(-50%, -50%) scale(2) translateY(-30px);
              opacity: 0;
            }
          }
          
          @keyframes counterPulse {
            0% {
              transform: scale(1);
              color: white;
            }
            50% {
              transform: scale(1.2);
              color: #FFD700;
            }
            100% {
              transform: scale(1);
              color: white;
            }
          }
          
          @keyframes bounce {
            0%, 20%, 53%, 80%, 100% {
              transform: translateY(0);
            }
            40%, 43% {
              transform: translateY(-8px);
            }
            70% {
              transform: translateY(-4px);
            }
            90% {
              transform: translateY(-2px);
            }
          }
        `}
      </style>

      {/* 🐰 EASTER EGG SUCCESS POPUP (Mobile) */}
      {showEasterEggPopup && (
        <div style={{
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            background: theme.surface,
            border: `3px solid ${theme.primary}`,
            borderRadius: '16px',
            padding: '24px',
            textAlign: 'center',
            maxWidth: '90%',
            margin: '16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }}>
            <div style={{ 
              fontSize: '40px', 
              marginBottom: '12px',
            }}>
              🐰🥚
            </div>
            <div style={{ 
              fontSize: '20px', 
              fontWeight: 'bold', 
              color: theme.primary,
              marginBottom: '12px'
            }}>
              YOU FOUND THE EASTER EGG!
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: theme.text,
              marginBottom: '20px',
              lineHeight: '1.4'
            }}>
              🎉 Congratulations! You discovered the secret Easter Bunny prediction!<br/><br/>
              📸 <strong>TAKE A SCREENSHOT NOW!</strong><br/>
              Tweet it @proofofprice with your <strong>@MannaBitcoin wallet address</strong> to receive <strong>1000 SATS</strong>! 💰<br/><br/>
              📱 Need a wallet? Get one at: <a href="https://mannabitcoin.com/contact" target="_blank" rel="noopener noreferrer" style={{color: theme.primary, textDecoration: 'underline'}}>mannabitcoin.com/contact</a>
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button 
                onClick={() => setShowEasterEggPopup(false)}
                style={{
                  background: theme.primary,
                  color: theme.background,
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                }}
              >
                Awesome! 🚀
              </button>
              <button 
                onClick={() => {
                  // Reset the easter egg game
                  localStorage.removeItem('proofofprice-easter-egg-2026');
                  setShowEasterEggPopup(false);
                  setEasterEggCollected(0);
                  setEasterEggs([]);
                  setIsEasterEggHuntActive(false); // Reset hunt state so it can be activated again
                  easterEggSpawnedRef.current = false;
                  // Force re-trigger of the effect to start new game
                  setTimeout(() => {}, 100);
                }}
                style={{
                  background: 'transparent',
                  color: theme.primary,
                  border: `2px solid ${theme.primary}`,
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                }}
              >
                Play Again! 🔄
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🐰 EASTER EGG INSTRUCTIONS POPUP (Mobile) */}
      {showEasterEggInstructions && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: theme.surface,
          border: `3px solid ${theme.primary}`,
          borderRadius: '12px',
          padding: '16px 20px',
          textAlign: 'center',
          zIndex: 10001,
          boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
          maxWidth: '90%',
        }}>
          <div style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            color: theme.primary,
            marginBottom: '8px'
          }}>
            🐰 EASTER EGG HUNT! 🥚
          </div>
          <div style={{ 
            fontSize: '13px', 
            color: theme.text,
            lineHeight: '1.4'
          }}>
            Tap the falling eggs to collect them!<br/>
            Collect <strong>all 21 eggs</strong> to win 1000 sats! 🏆
          </div>
        </div>
      )}


    </div>
  );
};

export default MobilePriceChart;